/**
 * 路由配置
 * 定义应用中的所有路由路径和参数
 */
import React, { lazy } from 'react';

const HomePage = lazy(() => import('../pages/HomePage.js'));
const LocalVocabularyPage = lazy(() => import('../pages/LocalVocabularyPage.js'));
const HistoryPage = lazy(() => import('../pages/HistoryPage.js'));
const LoginPage = lazy(() => import('../pages/login.js'));
const RegisterPage = lazy(() => import('../pages/register.js'));
const ProfilePage = lazy(() => import('../pages/profile.js'));
const PricingPage = lazy(() => import('../pages/PricingPage.js'));
const ForgotPasswordPage = lazy(() =>
  import('../pages/ForgotPasswordPage.js').then(module => ({ default: module.ForgotPasswordPage }))
);
const ResetPasswordPage = lazy(() =>
  import('../pages/ResetPasswordPage.js').then(module => ({ default: module.ResetPasswordPage }))
);
const AuthCallbackPage = lazy(() =>
  import('../pages/AuthCallbackPage.js').then(module => ({ default: module.AuthCallbackPage }))
);
const PaymentSuccessPage = lazy(() =>
  import('../pages/PaymentSuccessPage.js').then(module => ({ default: module.PaymentSuccessPage }))
);
const PaymentCancelPage = lazy(() =>
  import('../pages/PaymentCancelPage.js').then(module => ({ default: module.PaymentCancelPage }))
);
const NotFoundPage = lazy(() =>
  import('../pages/NotFoundPage.js').then(module => ({ default: module.NotFoundPage }))
);
const TermsOfServicePage = lazy(() => import('../features/legal/TermsOfServicePage.js'));
const PrivacyPolicyPage = lazy(() => import('../features/legal/PrivacyPolicyPage.js'));
const DocumentActivatorRoute = lazy(() => import('./DocumentActivatorRoute.js'));
import { RouteAuthGuard } from './RouteAuthGuard.js';
import { RouteGuestGuard } from './RouteGuestGuard.js';

// 路由名称枚举
export enum RouteNames {
  HOME = 'HOME',

  DOCUMENT_READ = 'DOCUMENT_READ', // New unified route
  LOCAL_VOCABULARY = 'LOCAL_VOCABULARY',
  HISTORY = 'HISTORY',
  // 新增认证路由
  LOGIN = 'LOGIN',
  REGISTER = 'REGISTER',
  PROFILE = 'PROFILE',
  PRICING = 'PRICING',
  FORGOT_PASSWORD = 'FORGOT_PASSWORD',
  RESET_PASSWORD = 'RESET_PASSWORD',
  AUTH_CALLBACK = 'AUTH_CALLBACK',
  // 支付流程路由
  PAYMENT_SUCCESS = 'PAYMENT_SUCCESS',
  PAYMENT_CANCEL = 'PAYMENT_CANCEL',
  // Legal Pages
  TERMS_OF_SERVICE = 'TERMS_OF_SERVICE',
  PRIVACY_POLICY = 'PRIVACY_POLICY',
  NOT_FOUND = 'NOT_FOUND',
}

// 路由参数接口
export interface RouteParams {
  [RouteNames.HOME]: Record<string, never>;
  [RouteNames.DOCUMENT_READ]: {
    identityKey: string;
  };
  [RouteNames.LOCAL_VOCABULARY]: Record<string, never>;
  [RouteNames.HISTORY]: Record<string, never>;
  // 为其他路由添加参数
  [RouteNames.LOGIN]: Record<string, never>;
  [RouteNames.REGISTER]: Record<string, never>;
  [RouteNames.PROFILE]: Record<string, never>;
  [RouteNames.PRICING]: Record<string, never>;
  [RouteNames.FORGOT_PASSWORD]: Record<string, never>;
  [RouteNames.RESET_PASSWORD]: Record<string, never>;
  [RouteNames.AUTH_CALLBACK]: Record<string, never>;
  [RouteNames.PAYMENT_SUCCESS]: Record<string, never>;
  [RouteNames.PAYMENT_CANCEL]: Record<string, never>;
  [RouteNames.TERMS_OF_SERVICE]: Record<string, never>;
  [RouteNames.PRIVACY_POLICY]: Record<string, never>;
  [RouteNames.NOT_FOUND]: Record<string, never>;
}

// 新增：路由配置对象的接口
import { routeDefinitions } from './routePaths.js';

export interface RouteConfig {
  name: RouteNames;
  path: string;
  component: React.LazyExoticComponent<React.ComponentType<object>>;
  guard?: React.ComponentType<{ children: React.ReactNode }>;
  indexable?: boolean; // New field for SEO indexing
}

// 路由配置 (结构调整)
// 注意：component 等属性需要您根据实际项目结构来填充
export const ROUTES: Record<RouteNames, RouteConfig> = {
  [RouteNames.HOME]: {
    name: RouteNames.HOME,
    ...routeDefinitions.HOME,
    component: HomePage,
  },

  [RouteNames.DOCUMENT_READ]: {
    name: RouteNames.DOCUMENT_READ,
    ...routeDefinitions.DOCUMENT_READ,
    component: DocumentActivatorRoute,
    guard: RouteAuthGuard,
  },
  [RouteNames.LOCAL_VOCABULARY]: {
    name: RouteNames.LOCAL_VOCABULARY,
    ...routeDefinitions.LOCAL_VOCABULARY,
    component: LocalVocabularyPage,
    guard: RouteAuthGuard,
  },
  [RouteNames.HISTORY]: {
    name: RouteNames.HISTORY,
    ...routeDefinitions.HISTORY,
    component: HistoryPage,
    guard: RouteAuthGuard,
  },
  // 新增的认证路由
  [RouteNames.LOGIN]: {
    name: RouteNames.LOGIN,
    ...routeDefinitions.LOGIN,
    component: LoginPage,
    guard: RouteGuestGuard,
  },
  [RouteNames.REGISTER]: {
    name: RouteNames.REGISTER,
    ...routeDefinitions.REGISTER,
    component: RegisterPage,
    guard: RouteGuestGuard,
  },
  [RouteNames.PROFILE]: {
    name: RouteNames.PROFILE,
    ...routeDefinitions.PROFILE,
    component: ProfilePage,
    guard: RouteAuthGuard,
  },
  // 新增密码重置流程路由
  [RouteNames.FORGOT_PASSWORD]: {
    name: RouteNames.FORGOT_PASSWORD,
    ...routeDefinitions.FORGOT_PASSWORD,
    component: ForgotPasswordPage,
    guard: RouteGuestGuard,
  },
  [RouteNames.RESET_PASSWORD]: {
    name: RouteNames.RESET_PASSWORD,
    ...routeDefinitions.RESET_PASSWORD,
    component: ResetPasswordPage,
  },
  // 新增认证回调路由
  [RouteNames.PRICING]: {
    name: RouteNames.PRICING,
    ...routeDefinitions.PRICING,
    component: PricingPage,
  },
  [RouteNames.AUTH_CALLBACK]: {
    name: RouteNames.AUTH_CALLBACK,
    ...routeDefinitions.AUTH_CALLBACK,
    component: AuthCallbackPage,
  },
  // 支付结果页
  [RouteNames.PAYMENT_SUCCESS]: {
    name: RouteNames.PAYMENT_SUCCESS,
    ...routeDefinitions.PAYMENT_SUCCESS,
    component: PaymentSuccessPage,
  },
  [RouteNames.PAYMENT_CANCEL]: {
    name: RouteNames.PAYMENT_CANCEL,
    ...routeDefinitions.PAYMENT_CANCEL,
    component: PaymentCancelPage,
  },
  // Legal Pages
  [RouteNames.TERMS_OF_SERVICE]: {
    name: RouteNames.TERMS_OF_SERVICE,
    ...routeDefinitions.TERMS_OF_SERVICE,
    component: TermsOfServicePage,
  },
  [RouteNames.PRIVACY_POLICY]: {
    name: RouteNames.PRIVACY_POLICY,
    ...routeDefinitions.PRIVACY_POLICY,
    component: PrivacyPolicyPage,
  },
  [RouteNames.NOT_FOUND]: {
    name: RouteNames.NOT_FOUND,
    ...routeDefinitions.NOT_FOUND,
    component: NotFoundPage,
  },
};

// 获取路由路径的辅助函数 (更新以支持路径参数替换)
export function getRoutePath<T extends RouteNames>(name: T, params?: RouteParams[T]): string {
  let path = routeDefinitions[name].path;

  // 如果没有参数，直接返回路径
  if (!params) {
    return path;
  }

  // 确保 params 是一个对象并且不是 null
  if (typeof params === 'object' && params !== null) {
    for (const key in params) {
      // 确保 key 是 params 自身的属性，并且 params 对象确实有这个 key
      if (Object.prototype.hasOwnProperty.call(params, key)) {
        // 断言 key 是 params 的一个键
        const typedKey = key as keyof typeof params;
        const value = params[typedKey]; // 现在 TypeScript 知道 key 是 params 的有效键
        const placeholder = `:${String(typedKey)}`; // key 可能是 symbol，所以用 String()

        // 确保 value 不是 undefined 或 null 才进行替换
        if (value !== undefined && value !== null && path.includes(placeholder)) {
          path = path.replace(placeholder, String(value));
        }
      }
    }
  }

  return path;
}
